import { capturePostHogEvent } from "./posthog";
import { captureException } from "./sentry";
import { supabase } from "./supabase";

// ---------------------------------------------------------------------------
// PII Sanitizer
// ---------------------------------------------------------------------------

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const API_KEY_RE = /\b(sk-[a-zA-Z0-9_-]{20,}|sk-ant-[a-zA-Z0-9_-]{20,}|AIza[a-zA-Z0-9_-]{20,})\b/g;

/** Strip email addresses and API keys from event properties (deep). */
export function sanitizePII(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === "string") {
      out[key] = value.replace(EMAIL_RE, "[EMAIL]").replace(API_KEY_RE, "[API_KEY]");
    } else if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      out[key] = sanitizePII(value as Record<string, unknown>);
    } else {
      out[key] = value;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Session ID (per-tab, via sessionStorage)
// ---------------------------------------------------------------------------

const SESSION_KEY = "sm_analytics_session";

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // SSR or private-mode fallback
    return "unknown";
  }
}

// ---------------------------------------------------------------------------
// Device type from viewport width
// ---------------------------------------------------------------------------

export type DeviceType = "mobile" | "tablet" | "desktop";

function getDeviceType(): DeviceType {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

// ---------------------------------------------------------------------------
// Batched Tracker
// ---------------------------------------------------------------------------

interface AnalyticsEvent {
  event_name: string;
  metadata: Record<string, unknown>;
  session_id: string;
  device_type: DeviceType;
  timestamp: string;
}

const FLUSH_INTERVAL_MS = 5_000;
const BATCH_SIZE = 20;
const MAX_QUEUE_SIZE = 200;

// Events permitted to write to public.analytics_events. Must stay in sync with
// the RLS allowlist in supabase/migrations/20260714160000_byok_schema_teardown.sql
// (which superseded 20260506130000, removing the byok_* events).
// Drift causes Postgrest 42501 RLS denials in prod (see tpc-d8c).
export const FIRST_PARTY_EVENT_ALLOWLIST = new Set([
  "signup_complete",
  "login_complete",
  "logout",
  "skill_added",
  "quick_import_complete",
  "resume_uploaded",
  "skills_auto_extracted",
  "mining_session_start",
  "mining_session_complete",
  "match_score_explained",
  "skill_gap_analysis_requested",
  "application_submitted",
  "application_status_changed",
  "application_status_updated",
  "resume_generation_start",
  "resume_generation_complete",
  "resume_generated",
  "resume_downloaded",
  "swipe_completed",
  "first_swipe",
  "starter_tier_activated",
  "session_start",
]);

let buffer: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let optedOut: boolean | null = null; // null = not yet checked
let currentUserId: string | null = null;

async function resolveOptOut(userId: string): Promise<boolean> {
  try {
    const { data } = await supabase
      .from("user_profiles")
      .select("analytics_opt_out")
      .eq("user_id", userId)
      .maybeSingle();

    return data?.analytics_opt_out === true;
  } catch {
    return false;
  }
}

/** Flush the buffer to Supabase. Retries once on failure then re-queues. */
async function flush(): Promise<void> {
  if (buffer.length === 0) return;

  let userId: string;
  try {
    // Always verify the active session before each flush. Without this,
    // a stale currentUserId persists after sign-out / token expiry and the
    // insert is rejected by RLS with 42501 (auth.uid() is null but
    // user_id still references the prior user). See tpc-e8f.
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.user) {
      // No active session — events would fail RLS. Hold the buffer; if
      // the user signs in we'll flush then. MAX_QUEUE_SIZE caps growth.
      return;
    }

    if (session.user.id !== currentUserId) {
      // User changed (sign-in, or sign-out + sign-in as a different user).
      // Drop buffered events from the prior user so we don't attribute
      // their activity to the new user.
      if (currentUserId !== null) {
        buffer = [];
      }
      currentUserId = session.user.id;
      optedOut = null;
    }
    userId = currentUserId;

    if (optedOut === null) {
      optedOut = await resolveOptOut(userId);
    }
    if (optedOut) {
      buffer = [];
      return;
    }
  } catch (err) {
    void captureException(err, { tags: { module: "analytics" } });
    return;
  }

  if (buffer.length === 0) return;

  const batch = buffer.splice(0, BATCH_SIZE);

  const rows = batch.map((evt) => ({
    user_id: userId,
    event_name: evt.event_name,
    metadata: {
      ...evt.metadata,
      session_id: evt.session_id,
      device_type: evt.device_type,
      timestamp: evt.timestamp,
    },
  }));

  try {
    const { error } = await supabase.from("analytics_events").insert(rows);
    if (error) throw error;
  } catch (err) {
    void captureException(err, { tags: { module: "analytics" } });
    // Re-queue failed batch (respect cap)
    const requeued = [...batch, ...buffer];
    buffer = requeued.slice(0, MAX_QUEUE_SIZE);
  }
}

function ensureTimer(): void {
  if (flushTimer !== null) return;
  flushTimer = setInterval(() => {
    flush().catch(() => {});
  }, FLUSH_INTERVAL_MS);
}

function enqueue(eventName: string, metadata: Record<string, unknown>): void {
  const sanitizedMetadata = sanitizePII(metadata);

  void capturePostHogEvent(eventName, sanitizedMetadata);

  if (!FIRST_PARTY_EVENT_ALLOWLIST.has(eventName)) {
    return;
  }

  if (buffer.length >= MAX_QUEUE_SIZE) return; // drop oldest prevention: just stop accepting

  buffer.push({
    event_name: eventName,
    metadata: sanitizedMetadata,
    session_id: getSessionId(),
    device_type: getDeviceType(),
    timestamp: new Date().toISOString(),
  });

  if (buffer.length >= BATCH_SIZE) {
    flush().catch(() => {});
  }
  ensureTimer();
}

// Flush on tab hide (visibilitychange)
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flush().catch(() => {});
    }
  });
}

// ---------------------------------------------------------------------------
// Public API — backward-compatible trackEvent
// ---------------------------------------------------------------------------

/**
 * Track an analytics event. Fire-and-forget — never throws.
 * Events are batched and flushed every 5s or at 20 events.
 * PII (emails, API keys) is automatically stripped from metadata.
 */
export async function trackEvent(
  eventName: string,
  metadata: Record<string, unknown> = {},
): Promise<void> {
  try {
    enqueue(eventName, metadata);
  } catch (err) {
    void captureException(err, { tags: { module: "analytics" } });
  }
}

/** Force-flush the buffer. Useful for testing or before navigation. */
export async function flushAnalytics(): Promise<void> {
  await flush();
}

/** Reset tracker state. For testing only. */
export function _resetForTesting(): void {
  buffer = [];
  optedOut = null;
  currentUserId = null;
  if (flushTimer !== null) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}

/** Expose buffer length for testing. */
export function _getBufferSize(): number {
  return buffer.length;
}

// ---------------------------------------------------------------------------
// Typed convenience wrappers
// ---------------------------------------------------------------------------

export const analytics = {
  auth: {
    signupStarted: (meta: { method: string }) => trackEvent("signup_started", meta),
    signupComplete: (meta: { method: string }) => trackEvent("signup_complete", meta),
    loginComplete: (meta: { method: string }) => trackEvent("login_complete", meta),
    logout: () => trackEvent("logout"),
  },

  skills: {
    skillAdded: (meta: { skill_id: string; source: string }) => trackEvent("skill_added", meta),
    skillRemoved: (meta: { skill_id: string }) => trackEvent("skill_removed", meta),
    quickImportStart: () => trackEvent("quick_import_start"),
    quickImportComplete: (meta: { skillsAdded: number }) =>
      trackEvent("quick_import_complete", meta),
    quickImportSkip: () => trackEvent("quick_import_skip"),
    resumeUploaded: (meta: { source: "file" | "paste" }) => trackEvent("resume_uploaded", meta),
    autoExtracted: (meta: { skill_count: number }) => trackEvent("skills_auto_extracted", meta),
    graphFirstView: () => trackEvent("skill_graph_first_view"),
  },

  mining: {
    sessionStart: (
      meta: {
        mode?: string;
        session_id?: string;
        source?: string;
        focused_skill_name?: string;
        document_attached?: boolean;
      } = {},
    ) => trackEvent("mining_session_start", meta),
    sessionComplete: (meta: {
      skills_extracted: number;
      stories_saved?: number;
      session_id?: string;
      mode?: string;
      source?: string;
    }) => trackEvent("mining_session_complete", meta),
    sessionStartFailed: (meta: {
      mode?: string;
      source?: string;
      focused_skill_name?: string;
      error_message: string;
      error_code?: string;
    }) => trackEvent("mining_session_start_failed", meta),
    messageFailed: (meta: {
      session_id?: string;
      topic?: string;
      error_type?: string | null;
      error_message: string;
      recovery_saved?: boolean;
      active_document?: boolean;
    }) => trackEvent("mining_message_failed", meta),
    recoveryShown: (meta: { session_id?: string; reason?: string }) =>
      trackEvent("mining_recovery_shown", meta),
    rollback: (meta: Record<string, unknown>) => trackEvent("mining_rollback", meta),
    demoCreditsExhausted: (meta: Record<string, unknown>) =>
      trackEvent("demo_credits_exhausted", meta),
  },

  matching: {
    matchViewed: (meta: { job_id: string; tier: number }) => trackEvent("match_viewed", meta),
    matchScoreExplained: (meta: { job_id: string }) => trackEvent("match_score_explained", meta),
    skillGapAnalysisRequested: (meta: { job_id: string }) =>
      trackEvent("skill_gap_analysis_requested", meta),
  },

  applications: {
    applied: (meta: { job_id: string }) => trackEvent("application_submitted", meta),
    statusChanged: (meta: { job_id: string; from: string; to: string }) =>
      trackEvent("application_status_changed", meta),
    statusUpdated: (meta: { job_id: string; old_status: string; new_status: string }) =>
      trackEvent("application_status_updated", meta),
  },

  resume: {
    generationStart: (meta: {
      format: string;
      tailored: boolean;
      source: "base" | "job" | "paste";
      job_id?: string;
    }) => trackEvent("resume_generation_start", meta),
    generationComplete: (meta: {
      format: string;
      tailored: boolean;
      source: "base" | "job" | "paste";
      job_id?: string;
    }) => trackEvent("resume_generation_complete", meta),
    generationFailed: (meta: {
      format: string;
      tailored: boolean;
      source: "base" | "job" | "paste";
      error_message: string;
      error_code?: string;
      job_id?: string;
    }) => trackEvent("resume_generation_failed", meta),
    saveCompleted: (meta: { resume_id?: string; source: "create" | "update" }) =>
      trackEvent("resume_save_completed", meta),
    saveFailed: (meta: {
      source: "create" | "update";
      error_message: string;
      error_code?: string;
      resume_id?: string | null;
    }) => trackEvent("resume_save_failed", meta),
    generated: (meta: {
      provider: string;
      format: string;
      tailored: boolean;
      source?: "base" | "job" | "paste";
      job_id?: string;
    }) => trackEvent("resume_generated", meta),
    downloaded: (meta: { format: string; resume_id?: string | null; source?: string }) =>
      trackEvent("resume_downloaded", meta),
  },

  swipe: {
    completed: (meta: { direction: string; tier: number; time_on_card_ms: number }) =>
      trackEvent("swipe_completed", meta),
    cardViewed: (meta: { job_id: string; position_in_deck: number; tier: number }) =>
      trackEvent("card_viewed", meta),
    cardExpanded: (meta: { job_id: string; time_on_card_ms: number }) =>
      trackEvent("card_expanded", meta),
  },

  tier: {
    starterActivated: () => trackEvent("starter_tier_activated"),
  },

  proUpgrade: {
    pageViewed: (meta: { source?: string } = {}) => trackEvent("pro_upgrade_page_viewed", meta),
    ctaClicked: (meta: { source?: string } = {}) => trackEvent("pro_upgrade_cta_clicked", meta),
    upsellShown: (meta: { source: string; recommended?: boolean }) =>
      trackEvent("pro_upgrade_upsell_shown", meta),
    upsellClicked: (meta: { source: string }) => trackEvent("pro_upgrade_upsell_clicked", meta),
    checkoutStarted: (meta: { source?: string; price_cents: number }) =>
      trackEvent("pro_upgrade_checkout_started", meta),
    checkoutRedirected: (meta: { source?: string; session_id: string; price_cents: number }) =>
      trackEvent("pro_upgrade_checkout_redirected", meta),
    checkoutFailed: (meta: {
      source?: string;
      error_message: string;
      error_code?: string;
      status?: number;
    }) => trackEvent("pro_upgrade_checkout_failed", meta),
    portalOpened: (meta: { source?: string }) => trackEvent("pro_upgrade_portal_opened", meta),
    portalFailed: (meta: {
      source?: string;
      error_message: string;
      error_code?: string;
      status?: number;
    }) => trackEvent("pro_upgrade_portal_failed", meta),
  },

  purchase: {
    modalShown: (meta: {
      credits_remaining: number;
      credits_used?: number;
      monthly_limit: number;
      source?: string;
      limit_hit_months?: number;
      pro_recommended?: boolean;
    }) => trackEvent("purchase_modal_shown", meta),
    checkoutStarted: (meta: { credits: number; price_cents: number }) =>
      trackEvent("purchase_checkout_started", meta),
    checkoutRedirected: (meta: { session_id: string; credits: number; price_cents: number }) =>
      trackEvent("purchase_checkout_redirected", meta),
    checkoutFailed: (meta: { error: string; error_code?: string; status?: number }) =>
      trackEvent("purchase_checkout_failed", meta),
    completed: (meta: { credits: number }) => trackEvent("purchase_completed", meta),
    modalDismissed: () => trackEvent("purchase_modal_dismissed"),
  },

  admin: {
    userLookup: (meta: {
      target_lookup_type: "email" | "user_id";
      success: boolean;
      target_user_id?: string;
      error_code?: string;
    }) => trackEvent("admin_user_lookup", meta),
    userCreated: (meta: {
      target_user_id?: string;
      email_confirmed: boolean;
      success: boolean;
      reason: string;
      error_code?: string;
    }) => trackEvent("admin_user_created", meta),
    proGranted: (meta: {
      target_user_id?: string;
      reason: string;
      success: boolean;
      error_code?: string;
    }) => trackEvent("admin_user_pro_granted", meta),
    proRevoked: (meta: {
      target_user_id?: string;
      reason: string;
      success: boolean;
      error_code?: string;
    }) => trackEvent("admin_user_pro_revoked", meta),
    adminGranted: (meta: {
      target_user_id?: string;
      reason: string;
      success: boolean;
      error_code?: string;
    }) => trackEvent("admin_user_admin_granted", meta),
    adminRevoked: (meta: {
      target_user_id?: string;
      reason: string;
      success: boolean;
      error_code?: string;
    }) => trackEvent("admin_user_admin_revoked", meta),
    creditsGranted: (meta: {
      target_user_id?: string;
      credits: number;
      reason: string;
      success: boolean;
      error_code?: string;
    }) => trackEvent("admin_user_credits_granted", meta),
    verificationResent: (meta: {
      target_user_id?: string;
      reason: string;
      success: boolean;
      error_code?: string;
    }) => trackEvent("admin_user_verification_resent", meta),
    emailConfirmed: (meta: {
      target_user_id?: string;
      reason: string;
      success: boolean;
      error_code?: string;
    }) => trackEvent("admin_user_email_confirmed", meta),
    passwordResetSent: (meta: {
      target_user_id?: string;
      reason: string;
      success: boolean;
      error_code?: string;
    }) => trackEvent("admin_user_password_reset_sent", meta),
    passwordSet: (meta: {
      target_user_id?: string;
      reason: string;
      success: boolean;
      error_code?: string;
    }) => trackEvent("admin_user_password_set", meta),
    requestLookup: (meta: {
      request_id: string;
      success: boolean;
      matched_user_id?: string;
      match_count?: number;
      error_code?: string;
    }) => trackEvent("admin_request_lookup", meta),
    probeRun: (meta: {
      mode: "starter" | "pro";
      success: boolean;
      status?: number;
      request_id?: string;
      error_code?: string;
    }) => trackEvent("admin_analysis_probe_run", meta),
    refreshBatchStarted: (meta: {
      source: "dashboard";
      max_companies: number;
      refresh_all: boolean;
    }) => trackEvent("admin_refresh_batch_started", meta),
    refreshBatchCompleted: (meta: {
      source: "dashboard";
      refresh_id: string;
      companies_checked: number;
      companies_refreshed: number;
      companies_failed: number;
    }) => trackEvent("admin_refresh_batch_completed", meta),
    refreshBatchFailed: (meta: { source: "dashboard"; error_message: string }) =>
      trackEvent("admin_refresh_batch_failed", meta),
    refreshBatchCanceled: (meta: { source: "dashboard"; refresh_id: string }) =>
      trackEvent("admin_refresh_batch_canceled", meta),
  },

  engagement: {
    sessionStart: () => trackEvent("session_start"),
  },

  // Beta-access foundation events. PostHog-only for now: they are
  // intentionally NOT in FIRST_PARTY_EVENT_ALLOWLIST, so no RLS allowlist
  // migration (20260506130000 sync pattern) is needed. Add them to both
  // lists together if first-party persistence becomes necessary.
  beta: {
    waitlistJoined: (meta: { has_use_case: boolean }) => trackEvent("waitlist_joined", meta),
    granted: (meta: { target_user_id?: string; success: boolean; error_code?: string }) =>
      trackEvent("beta_granted", meta),
    feedbackSubmitted: (meta: { category: string; page_context?: string }) =>
      trackEvent("beta_feedback_submitted", meta),
  },

  share: {
    promptShown: (meta: { trigger: string; skill_count: number }) =>
      trackEvent("share_prompt_shown", meta),
    promptDismissed: (meta: { trigger: string }) => trackEvent("share_prompt_dismissed", meta),
    promptAction: (meta: { trigger: string; action: string }) =>
      trackEvent("share_prompt_action", meta),
    graphViewed: (meta: { referrer_user_id: string }) => trackEvent("shared_graph_viewed", meta),
    ctaClicked: (meta: { referrer_user_id: string }) =>
      trackEvent("shared_graph_cta_clicked", meta),
    linkedinShare: (meta: { source: string }) => trackEvent("linkedin_share", meta),
  },

  referral: {
    attributed: (meta: { referrer_user_id: string }) => trackEvent("referral_attributed", meta),
  },

  experiments: {
    variantAssigned: (meta: { experiment_key: string; variant: string }) =>
      trackEvent("experiment_variant_assigned", meta),
    variantExposure: (meta: { experiment_key: string; variant: string; context: string }) =>
      trackEvent("experiment_variant_exposure", meta),
  },

  extraction: {
    consentShown: (meta: {
      total_jobs: number;
      credits_needed: number;
      credits_remaining: number;
      can_afford_all: boolean;
    }) => trackEvent("extraction_consent_shown", meta),
    consentAccepted: (meta: { total_jobs: number; mode: string; partial_count?: number }) =>
      trackEvent("extraction_consent_accepted", meta),
    consentSkipped: (meta: { total_jobs: number }) =>
      trackEvent("extraction_consent_skipped", meta),
    autoExtracted: (meta: { total_jobs: number; reason: string }) =>
      trackEvent("extraction_auto_extracted", meta),
  },

  jobAnalysis: {
    clicked: (meta: {
      job_id: string;
      company_id: string;
      request_id: string;
      user_tier: string;
      credits_remaining_before: number;
    }) => trackEvent("job_analysis_clicked", meta),
    started: (meta: {
      job_id: string;
      company_id: string;
      request_id: string;
      user_tier: string;
      provider_requested: string | null;
      purpose: string;
      credits_remaining_before: number;
    }) => trackEvent("job_analysis_started", meta),
    failed: (meta: {
      job_id: string;
      company_id: string;
      request_id: string;
      user_tier: string;
      error_message: string;
      error_code?: string;
      credits_remaining_before: number;
      credits_remaining_after?: number;
    }) => trackEvent("job_analysis_failed", meta),
    succeeded: (meta: {
      job_id: string;
      company_id: string;
      request_id: string;
      user_tier: string;
      skill_count: number;
      cached: boolean;
      credits_remaining_before: number;
      credits_remaining_after: number;
    }) => trackEvent("job_analysis_succeeded", meta),
  },

  navigation: {
    // page_view was removed 2026-06-12: it double-counted navigation
    // against PostHogBridge's canonical $pageview and was never in the
    // first-party allowlist.
    tabSwitch: (meta: { from: string; to: string }) => trackEvent("tab_switch", meta),
    hiddenStrengthExpand: (meta: Record<string, unknown>) =>
      trackEvent("hidden_strength_expand", meta),
    firstSwipe: (meta: { direction: string; tier: number }) => trackEvent("first_swipe", meta),
  },
} as const;
